// ============================================================
// AdminKnowledgeBasePage — M10.8 (PRD §6.1.I R-P0-I1..I2)
// ============================================================
// CRUD Knowledge Base (markdown) cho Ask AI Step 2 RAG.
//   - List + search + filter tag
//   - Create / edit (markdown textarea)
//   - Embed (chunk + embed, cost-sensitive → yêu cầu X-Admin-TOTP)
//   - Delete
// ============================================================

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';
// M47 (2026-04-25): thay textarea markdown bằng RichTextEditor WYSIWYG (giống M35 rules)
import RichTextEditor from '../components/RichTextEditor.jsx';
// M100 (2026-04-26): TOTP modal cho Re-embed all (cost-sensitive)
import AdminTotpReverifyModal from '../components/AdminTotpReverifyModal.jsx';
// M157b (2026-05-13): Import Excel KB hàng loạt
import ImportExcelAdminTotpDialog from '../components/ImportExcelAdminTotpDialog.jsx';

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

// M48: format độ dài content — nếu có ảnh base64, số ký tự rất lớn → hiển thị
// theo KB/MB cho dễ đọc. Dùng cho counter "Nội dung (N/25M)".
function formatKbLen(n) {
  if (n < 10_000) return `${n.toLocaleString('vi-VN')} ký tự / 25M`;
  if (n < 1_000_000) return `${Math.round(n / 1000).toLocaleString('vi-VN')} K / 25M`;
  return `${(n / 1_000_000).toFixed(2)} M / 25M ký tự`;
}

function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    try {
      const p = JSON.parse(tags);
      if (Array.isArray(p)) return p;
    } catch {}
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  }
  return [];
}

export default function AdminKnowledgeBasePage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'knowledge_base' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('knowledge_base');
    return false;
  })();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // { id?, title, content_md, tags }
  const [embedAllBusy, setEmbedAllBusy] = useState(false);
  const [reembedBusy, setReembedBusy] = useState(false); // M100
  const [reembedTotpOpen, setReembedTotpOpen] = useState(false); // M100 TOTP modal
  const [importing, setImporting] = useState(false); // M20
  // M157 (2026-05-13) — Tải toàn bộ KB xuống Excel (yêu cầu TOTP admin)
  const [exportTotpOpen, setExportTotpOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  // M157b (2026-05-13) — Import Excel KB hàng loạt
  const [importExcelOpen, setImportExcelOpen] = useState(false);

  // M17 — count KB chưa embed (chunks_count=0 hoặc undefined)
  const missingCount = items.filter(x => !x.chunks_count || x.chunks_count === 0).length;

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin mới truy cập được');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin]);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.kbList({ q: q.trim() });
      // BE trả {rows, total} (legacy KB service); dự phòng {items} hoặc array thẳng
      const list = Array.isArray(res) ? res : (res.items || res.rows || []);
      setItems(list);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được KB');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing({ id: null, title: '', content_md: '', tags: '' });
  }

  async function openEdit(id) {
    try {
      const row = await api.kbGet(id);
      setEditing({
        id: row.id,
        title: row.title || '',
        content_md: row.content_md || '',
        tags: parseTags(row.tags).join(', '),
        chunks_count: row.chunks_count,
        chunks_embedded: row.chunks_embedded,
        updated_at: row.updated_at,
      });
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không lấy được KB');
    }
  }

  async function save() {
    if (!editing) return;
    const title = editing.title.trim();
    const content_md = editing.content_md;
    if (title.length < 3) { toast.error('Tiêu đề ≥ 3 ký tự'); return; }
    if (title.length > 200) { toast.error('Tiêu đề ≤ 200 ký tự'); return; }
    if (content_md.length < 10) { toast.error('Nội dung ≥ 10 ký tự'); return; }
    // M48: tăng cap lên 25M chars để chứa base64 ảnh. BE cũng đã tăng theo.
    if (content_md.length > 25_000_000) { toast.error('Nội dung quá dài (≤ 25M ký tự, bao gồm base64 ảnh)'); return; }
    const tagsArr = editing.tags
      ? editing.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    const body = { title, content_md, tags: tagsArr };
    try {
      if (editing.id) {
        await api.kbUpdate(editing.id, body);
        toast.success('Đã cập nhật');
      } else {
        const res = await api.kbCreate(body);
        toast.success(`Đã tạo KB #${res.id}`);
      }
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    }
  }

  async function del(id) {
    const ok = await showConfirm({
      title: 'Xoá KB',
      message: 'Xoá KB này? Toàn bộ chunks sẽ bị xoá theo.',
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.kbDelete(id);
      toast.success('Đã xoá');
      await refresh();
      if (editing?.id === id) setEditing(null);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Xoá thất bại');
    }
  }

  // M20 (2026-04-24) — upload file .md/.txt/.docx → convert markdown
  async function onImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset để upload lại cùng file
    if (!file) return;
    const maxMb = 15;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`File > ${maxMb}MB — backend từ chối`);
      return;
    }
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['md', 'txt', 'docx', 'markdown'].includes(ext)) {
      toast.error('Chỉ nhận .md / .txt / .docx');
      return;
    }
    setImporting(true);
    const t = toast.loading(`Đang convert ${file.name}…`);
    try {
      const res = await api.kbImportFile(file);
      toast.dismiss(t);
      // Điền vào editor — giữ title cũ nếu đã có, không thì dùng suggested
      setEditing(prev => ({
        ...prev,
        content_md: res.content_md,
        title: prev.title?.trim() || res.suggested_title,
      }));
      // M48 + M58: toast thêm info về ảnh + compress
      const compressedInfo = res.image_compressed
        ? ` (${res.image_compressed} đã nén, tiết kiệm ${res.image_saved_mb} MB)`
        : '';
      const imgMsg = res.image_embedded
        ? ` · 🖼 ${res.image_embedded} ảnh${compressedInfo}`
        : '';
      toast.success(
        `✓ Convert xong: ${res.char_count.toLocaleString('vi-VN')} ký tự${imgMsg}. Kiểm tra nội dung trước khi Lưu.`,
        { duration: 5000 }
      );
      if (res.image_skipped > 0) {
        toast(
          `⚠️ ${res.image_skipped} ảnh bị bỏ qua (định dạng lạ như WMF/GIF, hoặc quá lớn ngay cả sau khi nén).`,
          { icon: '⚠️', duration: 6000 }
        );
      }
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.data?.error || err.message || 'Convert thất bại', { duration: 6000 });
    } finally {
      setImporting(false);
    }
  }

  async function embedMissingAll() {
    if (embedAllBusy) return;
    if (missingCount === 0) { toast('Tất cả KB đã embed', { icon: '✓' }); return; }
    const ok = await showConfirm({
      title: 'Embed KB',
      message: `Embed ${missingCount} KB chưa có chunks?\nCó thể tốn thời gian nếu doc dài.`,
      confirmText: 'Embed',
    });
    if (!ok) return;
    setEmbedAllBusy(true);
    const t = toast.loading(`Đang embed ${missingCount} KB…`);
    try {
      const res = await api.kbEmbedMissing();
      toast.dismiss(t);
      if (res.failed > 0) {
        toast.error(
          `Embed xong: ${res.succeeded}/${res.total} thành công, ${res.failed} fail · ${res.cost_vnd ?? 0}đ`,
          { duration: 7000 }
        );
      } else {
        toast.success(
          `✓ Đã embed ${res.succeeded}/${res.total} KB (${res.chunks_inserted ?? 0} chunks) · ${res.cost_vnd ?? 0}đ`,
          { duration: 5000 }
        );
      }
      await refresh();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.data?.error || err.message || 'Embed missing thất bại');
    } finally {
      setEmbedAllBusy(false);
    }
  }

  // ============================================================
  // M100 (2026-04-26) — Re-embed TẤT CẢ KB (kể cả đã embed trước đó).
  // Dùng khi vừa đổi chiến lược chunk (heading vs words) hoặc max_words → cần
  // re-chunk lại data cũ để áp setting mới. Cost-sensitive → yêu cầu TOTP admin.
  // ============================================================
  async function reembedAll() {
    if (reembedBusy) return;
    const total = items.length;
    if (total === 0) { toast('Chưa có KB nào để re-embed'); return; }
    const ok = await showConfirm({
      title: 'Re-embed tất cả KB',
      message: `Sẽ re-chunk + re-embed ${total} KB. Tốn 1 lần chi phí embed (~${(total * 0.5).toFixed(0)}-${(total * 2).toFixed(0)}k tokens × giá embedding-3-large). Tiếp tục?`,
      confirmText: 'Tiếp tục',
      dangerous: true,
    });
    if (!ok) return;
    // Lần đầu gọi không có TOTP → BE trả ADMIN_TOTP_REQUIRED → mở modal
    setReembedTotpOpen(true);
  }

  async function handleReembedAllTotp(code) {
    // Throw error nếu TOTP sai → modal sẽ hiển thị
    setReembedBusy(true);
    const t = toast.loading(`Đang re-embed ${items.length} KB… (có thể mất 1-2 phút)`);
    try {
      const res = await api.kbReembedAll({ adminTotp: code });
      toast.dismiss(t);
      if (res.failed > 0) {
        toast.error(
          `Re-embed: ${res.succeeded}/${res.total} OK, ${res.failed} fail · ${res.chunks_inserted} chunks · ${res.cost_vnd ?? 0}đ`,
          { duration: 8000 }
        );
      } else {
        toast.success(
          `✓ Re-embed ${res.succeeded}/${res.total} KB → ${res.chunks_inserted} chunks (${res.cost_vnd ?? 0}đ)`,
          { duration: 6000 }
        );
      }
      setReembedTotpOpen(false);
      await refresh();
    } catch (err) {
      toast.dismiss(t);
      // Re-throw để modal handle (vd TOTP sai → set badCode)
      throw err;
    } finally {
      setReembedBusy(false);
    }
  }

  // ============================================================
  // M157 (2026-05-13) — Tải toàn bộ KB xuống file JSON.
  // Cost-sensitive (lộ nội dung KB) → yêu cầu TOTP admin re-verify.
  // ============================================================
  function openExportAll() {
    if (items.length === 0) { toast('Chưa có KB nào để tải xuống'); return; }
    setExportTotpOpen(true);
  }

  async function handleExportAllTotp(code) {
    setExportBusy(true);
    const t = toast.loading(`Đang xuất ${items.length} KB…`);
    try {
      const { blob, filename } = await api.kbExportAll({ adminTotp: code });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Cho browser thời gian start download trước khi revoke
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.dismiss(t);
      toast.success(`✓ Đã tải xuống ${filename}`);
      setExportTotpOpen(false);
    } catch (err) {
      toast.dismiss(t);
      throw err; // để modal handle TOTP sai
    } finally {
      setExportBusy(false);
    }
  }

  async function embed(id) {
    // M16 (2026-04-23): gỡ TOTP — cost embed 1 KB ~500đ, admin quyết định khi nào re-embed.
    // TOTP chỉ giữ cho Rebuild ALL ở /admin/ai tab Embedding.
    try {
      const t = toast.loading('Đang embed...');
      const res = await api.kbEmbed(id);
      toast.dismiss(t);
      toast.success(
        `Đã embed ${res.chunks_inserted ?? '?'} chunks · ${res.cost_vnd ?? 0}đ`,
        { duration: 5000 }
      );
      await refresh();
      if (editing?.id === id) await openEdit(id);
    } catch (err) {
      const msg = err.data?.error || err.message || 'Embed thất bại';
      toast.error(msg);
    }
  }

  return (
    <div className="min-h-full p-4">
      <div className="max-w-7xl mx-auto">
        {/* HEADER — M148c mobile-first stack */}
        <header className="mb-4">
          <Link
            to="/admin"
            className="text-sm text-slate-500 hover:text-slate-700"
            data-testid="kb-back"
          >← Trang admin</Link>
          <div className="mt-1 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg md:text-xl font-semibold text-brand-700">Knowledge Base (Admin)</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Kho kiến thức markdown — dùng cho RAG khi NV "Hỏi đáp" (Step 2) & admin Chat AI
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={embedMissingAll}
              disabled={missingCount === 0 || embedAllBusy}
              data-testid="kb-embed-missing"
              title={missingCount === 0 ? 'Tất cả KB đã embed' : `Embed ${missingCount} KB chưa có chunks`}
              className="px-3 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {embedAllBusy ? '⏳ Đang embed…' : `⚡ Embed tất cả chưa embed (${missingCount})`}
            </button>
            {/* M100 (2026-04-26): Re-chunk + re-embed TẤT CẢ KB (kể cả đã embed)
                — dùng khi vừa đổi chunk_strategy hoặc max_words. Cost-sensitive → TOTP. */}
            <button
              type="button"
              onClick={reembedAll}
              disabled={reembedBusy}
              title="Re-chunk + re-embed TẤT CẢ KB. Dùng khi vừa đổi chiến lược chunk."
              className="px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {reembedBusy ? '⏳ Đang re-embed…' : '🔄 Re-embed tất cả'}
            </button>
            {/* M157b (2026-05-13): Tải toàn bộ KB xuống file Excel. Yêu cầu TOTP admin. */}
            <button
              type="button"
              onClick={openExportAll}
              disabled={exportBusy || items.length === 0}
              data-testid="kb-export-all"
              title={items.length === 0
                ? 'Chưa có KB để tải xuống'
                : `Tải toàn bộ ${items.length} KB xuống file Excel (yêu cầu TOTP admin)`}
              className="px-3 py-2 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {exportBusy ? '⏳ Đang xuất…' : `⬇ Tải Excel (${items.length})`}
            </button>
            {/* M157b (2026-05-13): Import hàng loạt KB từ file Excel. Yêu cầu TOTP admin. */}
            <button
              type="button"
              onClick={() => setImportExcelOpen(true)}
              data-testid="kb-import-excel"
              title="Import hàng loạt KB từ file Excel (yêu cầu TOTP admin)"
              className="px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-sm font-medium"
            >
              📤 Import Excel
            </button>
            <button
              type="button"
              onClick={openCreate}
              data-testid="kb-new"
              className="px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold shadow-sm"
            >➕ Thêm KB</button>
          </div>
        </header>

        {/* SEARCH */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 mb-4 flex items-center gap-2">
          <input
            type="search"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') refresh(); }}
            placeholder="Tìm theo tiêu đề hoặc nội dung..."
            className="flex-1 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
            data-testid="kb-search"
          />
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm"
            data-testid="kb-refresh"
          >🔍 Tìm</button>
        </div>

        {/* LIST — M148c hotfix: overflow-x-auto cho phép scroll ngang table trên mobile */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
          {loading && <div className="p-6 text-center text-sm text-slate-400">Đang tải…</div>}
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-400">
              Chưa có KB nào. Bấm "➕ Thêm KB" để tạo mới.
            </div>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="text-left py-2 px-3 font-semibold">#</th>
                  <th className="text-left py-2 px-3 font-semibold">Tiêu đề</th>
                  <th className="text-left py-2 px-3 font-semibold">Tags</th>
                  <th className="text-right py-2 px-3 font-semibold">Chunks</th>
                  <th className="text-left py-2 px-3 font-semibold">Cập nhật</th>
                  <th className="text-right py-2 px-3 font-semibold">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {items.map(row => {
                  const tags = parseTags(row.tags);
                  const embedded = row.chunks_embedded ?? 0;
                  const total = row.chunks_count ?? 0;
                  const isStale = total === 0 || embedded < total;
                  return (
                    <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`kb-row-${row.id}`}>
                      <td className="py-2 px-3 text-slate-400 font-mono text-xs">#{row.id}</td>
                      <td className="py-2 px-3 max-w-xs">
                        <div className="font-medium text-slate-800 truncate">{row.title}</div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {tags.length === 0 && <span className="text-xs text-slate-300">—</span>}
                          {tags.map((t, i) => (
                            <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                              #{t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-xs">
                        {total === 0 ? (
                          <span className="text-amber-600">⚠ chưa embed</span>
                        ) : (
                          <span className={isStale ? 'text-amber-600' : 'text-emerald-600'}>
                            {embedded}/{total}{isStale ? ' ⚠' : ' ✓'}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-500">{fmtTime(row.updated_at)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEdit(row.id)}
                          className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                          data-testid={`kb-edit-${row.id}`}
                        >✏️ Sửa</button>
                        <button
                          type="button"
                          onClick={() => embed(row.id)}
                          className="text-xs px-2 py-1 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 ml-1"
                          title="Chunk + embed (cost-sensitive, yêu cầu OTP)"
                          data-testid={`kb-embed-${row.id}`}
                        >🔄 Embed</button>
                        <button
                          type="button"
                          onClick={() => del(row.id)}
                          className="text-xs px-2 py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 ml-1"
                          data-testid={`kb-delete-${row.id}`}
                        >🗑</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
          data-testid="kb-edit-modal"
        >
          {/* M101 (2026-04-26): Modal flex column → header (h2+meta+title+tags+label)
              CỐ ĐỊNH ở trên, content body scroll riêng. Toolbar editor sticky top-0
              trong body scroll context (thông qua prop stickyToolbar). */}
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* === HEADER cố định === */}
            <div className="flex-shrink-0 p-6 pb-3 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800 mb-2">
                {editing.id ? `✏️ Sửa KB #${editing.id}` : '➕ Thêm KB mới'}
              </h2>
              {editing.id && (
                <div className="text-[11px] text-slate-500 mb-3">
                  Chunks: {editing.chunks_embedded ?? 0}/{editing.chunks_count ?? 0} · Cập nhật: {fmtTime(editing.updated_at)}
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Tiêu đề (3–200)</label>
                  <input
                    type="text"
                    value={editing.title}
                    onChange={e => setEditing({ ...editing, title: e.target.value })}
                    maxLength={200}
                    className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
                    data-testid="kb-title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Tags (ngăn cách bởi dấu phẩy)
                  </label>
                  <input
                    type="text"
                    value={editing.tags}
                    onChange={e => setEditing({ ...editing, tags: e.target.value })}
                    placeholder="ví dụ: sản phẩm, bảo hành, khuyến mãi"
                    className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
                    data-testid="kb-tags"
                  />
                </div>
              </div>
            </div>

            {/* === BODY scrollable (Nội dung label + RichTextEditor) === */}
            <div className="flex-1 overflow-y-auto p-6 pt-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Nội dung ({formatKbLen(editing.content_md.length)})
                  </label>
                  {/* M20 (2026-04-24) — Upload .md/.txt/.docx */}
                  <label
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer ${
                      importing
                        ? 'bg-slate-100 border-slate-300 text-slate-400 cursor-wait'
                        : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                    }`}
                    title="Chọn file .md / .txt / .docx → convert sang markdown và điền vào ô nội dung"
                  >
                    {importing ? '⏳ Đang convert…' : '📎 Nhập từ file'}
                    <input
                      type="file"
                      accept=".md,.txt,.docx,.markdown"
                      disabled={importing}
                      onChange={onImportFile}
                      className="hidden"
                      data-testid="kb-import-file"
                    />
                  </label>
                </div>
                {/* M47 (2026-04-25): Rich Text Editor WYSIWYG — giống M35 Rules Modules.
                    Hỗ trợ Bold/Italic/heading/list/link/blockquote/code, paste ảnh,
                    upload ảnh, resize 4 mức, lightbox fullscreen khi click.
                    M101 (2026-04-26): stickyToolbar=true → toolbar luôn visible khi scroll. */}
                <RichTextEditor
                  value={editing.content_md}
                  onChange={(html) => setEditing({ ...editing, content_md: html })}
                  placeholder="Viết nội dung ở đây — hỗ trợ Bold/Italic, heading, list, link, blockquote, code. Paste ảnh trực tiếp hoặc upload từ máy. Click ảnh để phóng to."
                  stickyToolbar={true}
                />
              </div>
            </div>

            {/* === FOOTER cố định === */}
            <div className="flex-shrink-0 flex justify-end gap-2 p-6 pt-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setEditing(null)}
                data-testid="kb-cancel"
                className="px-4 py-2 rounded-lg bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 text-sm font-medium"
              >Huỷ</button>
              {editing.id && (
                <button
                  onClick={() => embed(editing.id)}
                  data-testid="kb-embed-from-modal"
                  className="px-4 py-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-sm font-medium"
                >🔄 Embed lại</button>
              )}
              <button
                onClick={save}
                data-testid="kb-save"
                className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold"
              >{editing.id ? 'Lưu' : 'Tạo mới'}</button>
            </div>
          </div>
        </div>
      )}

      {/* M100 (2026-04-26): TOTP modal cho Re-embed all */}
      <AdminTotpReverifyModal
        open={reembedTotpOpen}
        title="Xác minh để Re-embed tất cả KB"
        description="Hành động này tốn chi phí embed — cần TOTP admin."
        onClose={() => setReembedTotpOpen(false)}
        onSubmit={handleReembedAllTotp}
      />

      {/* M157b (2026-05-13): TOTP modal cho Tải toàn bộ KB ra Excel */}
      <AdminTotpReverifyModal
        open={exportTotpOpen}
        title="Xác minh để tải toàn bộ KB ra Excel"
        description={`Tải xuống ${items.length} KB ra file Excel (.xlsx).\nLưu ý: Excel cell limit 32k ký tự — KB có ảnh base64 sẽ bị cắt; dùng UI "Sửa" để xem đầy đủ.`}
        onClose={() => setExportTotpOpen(false)}
        onSubmit={handleExportAllTotp}
      />

      {/* M157b (2026-05-13): Import Excel KB */}
      <ImportExcelAdminTotpDialog
        open={importExcelOpen}
        title="Import KB từ Excel"
        formatHelp={
          <>
            <div className="font-medium text-slate-800 mb-1">Định dạng yêu cầu</div>
            <div>File <code>.xlsx</code>, sheet đầu tiên, header hàng 1 gồm các cột:</div>
            <ul className="list-disc ml-5 mt-1 text-slate-700">
              <li><b>Tiêu đề</b> (bắt buộc, 3-200 ký tự)</li>
              <li><b>Tags</b> (tuỳ chọn, ngăn cách bởi dấu phẩy)</li>
              <li><b>Nội dung</b> (bắt buộc, ≥ 10 ký tự, markdown)</li>
            </ul>
            <div className="mt-1 text-slate-500">
              KB trùng tiêu đề với DB hoặc trong file sẽ bị <b>skip</b> (không ghi đè).
              KB mới sẽ được auto-embed sau khi tạo.
            </div>
          </>
        }
        onClose={() => setImportExcelOpen(false)}
        onDone={() => { refresh(); }}
        uploadFn={(file, totp) => api.kbImportExcel(file, totp)}
      />
    </div>
  );
}
